import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "../../competitionOperation"
import { penaltyUrl } from "./penaltyView"
const messageForCode = code => ({
  INVALID_INCIDENT_REVIEW_CONFLICT: "The match or review has changed. Reload the incident and review the current evidence before deciding.",
  INVALID_INCIDENT_CONTEXT_CHANGED: "This incident is no longer available as observed. Reload the current Main Draw context.",
  INVALID_INCIDENT_PHASE_OPERATION_IN_PROGRESS: "A phase change is still being applied to this competition, so nothing was saved. Save the decision again once it finishes.",
  INVALID_INCIDENT_REVIEW_RESOLVED: "This incident has already been reviewed. Reload its decision and linked penalty.",
  INVALID_INCIDENT_DECISION: "Review the incident, person and required reason before saving.",
  PENALTY_VERSION_CONFLICT: "This penalty changed since it was opened. Reload the current record before trying again.",
  PENALTY_CANCELLED: "This penalty is cancelled and cannot be changed or reactivated.",
  PENALTY_NOT_FOUND: "The penalty is no longer available in this competition.",
  INVALID_PENALTY_TRANSITION: "This action no longer applies to the current penalty. Reload the record.",
  INVALID_PENALTY_ACTION: "Review the action, reason and current record before saving.",
  INVALID_PENALTY_PARTICIPANT: "The selected player could not be verified in this competition. Reload the participant list.",
  // PRD 16 LR12 (TBD-14): the entry or the incident is a Tenis10 child's, riding on the parent's account
  INVALID_PENALTY_CHILD_ENTRY: "A Tenis10 child is never penalized: the penalty would be saved on the parent's account. Nothing was saved.",
  INVALID_PENALTY_DETAILS: "Review the participant, type, reason and dates before saving.",
  ERROR_PERMISSIONS: "Your account no longer has permission to create penalties.",
  OPERATION_PAYLOAD_CONFLICT: "This request has already been used. Check its saved result before continuing.",
}[code] || "The result could not be confirmed. Check the same request before creating another penalty.")
export default function usePenaltyCreation({ userId, eventId, competitionId, penaltyId, incidentId, review = false }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = incidentId ? `penalty-incident-${review ? "review" : "creation"}:${userId}:${eventId}:${competitionId}:${incidentId}` : penaltyId ? `penalty-action:${userId}:${eventId}:${competitionId}:${penaltyId}` : `penalty-creation:${userId}:${eventId}:${competitionId}`
  const active = useRef(null), [state, setState] = useState({})
  // Only save's first send passes `firstSubmission`; Check result and Retry pending updates re-POST the same operation, and a 4xx
  // there cannot rule out an earlier commit. Accounts publishes a refusal with the competition but without the event, so responses are
  // scoped by competition only; the default scope would ignore every socket refusal until the next status read.
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner.cancel()
    const runner = runCompetitionOperation({ operation, socket, responseTopic: "accounts-response", operationKind: "penalty-created", messageForCode, submit, firstSubmission,
      scopeKeys: ["competitionId"], read: operationId => get("/admin/penalty-creations/" + operationId) })
    const request = { operation, runner }; active.current = request
    setState({ status: "pending", operation })
    runner.promise.then(result => {
      if (active.current !== request || result.status === "cancelled") return
      setState({ ...result, operation })
      if (result.status === "error") {
        active.current = null
        try { sessionStorage.removeItem(key) } catch { /* The server retains the outcome. */ }
      }
    })
  }, [key, socket])
  useEffect(() => {
    setState({})
    if (userId) {
      try {
        const pending = JSON.parse(sessionStorage.getItem(key) || "null")
        if (pending?.penaltyId === penaltyId && pending?.incidentId === incidentId && pending?.eventId === eventId && pending.competitionId === competitionId && /^[a-zA-Z0-9-]{16,128}$/.test(pending.operationId || "") && pending.correlationId && pending.payload) observe(pending)
      } catch { /* Invalid recovery records never send a command. */ }
    }
    return () => { active.current?.runner.cancel(); active.current = null }
  }, [userId, eventId, competitionId, penaltyId, incidentId, key, observe])
  const send = operation => () => post(penaltyUrl(competitionId) + (review ? "/incident-reviews" : penaltyId ? `/${penaltyId}/actions` : ""), { ...operation.payload, operationId: operation.operationId, socketClientId }, { headers: { "X-Correlation-Id": operation.correlationId } })
  const save = payload => {
    if (active.current || !userId) return
    const operation = { eventId, competitionId, ...(penaltyId && { penaltyId }), ...(incidentId && { incidentId }), operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), payload }
    try { sessionStorage.setItem(key, JSON.stringify(operation)) } catch {
      setState({ status: "error", message: "Enable browser session storage to recover the result after reload." }); return
    }
    observe(operation, send(operation), true)
  }
  const recover = () => { if (state.operation) observe(state.operation, send(state.operation)) }
  const reset = () => {
    active.current?.runner.cancel(); active.current = null
    try { sessionStorage.removeItem(key) } catch { /* No new operation until save succeeds. */ }
    setState({})
  }
  return { ...state, save, recover, reset, pending: ["pending", "unknown"].includes(state.status) }
}
